import type { ChangeEvent } from "react";
import { useRef } from "react";
import { FileUp, LayoutGrid, Palette, Share2, Tags, Type } from "lucide-react";
import { useTraitConnections } from "./TraitConnectionsContext";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { PresetSelector } from "../reusable/PresetSelector";
import { SendSvgToToolMenu } from "../reusable/SendSvgToToolMenu";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";

export function TraitConnectionsControls() {
    const {
        data,
        nodes,
        arcs,
        traitColors,
        nodeSize,
        nodeColor,
        arcStrokeWidth,
        showNodeLabels,
        showArcLabels,
        bezierAngle,
        error,
        loadDataFile,
        clearData,
        setNodeSize,
        setNodeColor,
        setArcStrokeWidth,
        setShowNodeLabels,
        setShowArcLabels,
        arcLabelFontSize,
        setArcLabelFontSize,
        arcLabelUseArcColor,
        setArcLabelUseArcColor,
        setBezierAngle,
        initialLayoutType,
        setInitialLayoutType,
        enableProximityLayout,
        proximityStrength,
        setEnableProximityLayout,
        setProximityStrength,
        reorderByTraitCount,
        setReorderByTraitCount,
        buildExportSvgString,
        handleDownload,
        handleDownloadHtml,
        applyPreset,
        getPresetValues,
        resetParameters,
    } = useTraitConnections();

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            loadDataFile(file);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleNodeSizeChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue <= 0) return;
        setNodeSize(numValue);
    };

    const handleArcStrokeWidthChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue <= 0) return;
        setArcStrokeWidth(numValue);
    };

    const handleBezierAngleChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue)) return;
        setBezierAngle(numValue);
    };

    const handleProximityStrengthChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue)) return;
        const clamped = Math.max(0, Math.min(100, numValue));
        setProximityStrength(clamped);
    };

    const uniqueTraits = Object.keys(traitColors);
    const uniqueTraitCount = uniqueTraits.length;
    const hasData = data.length > 0;

    return (
        <div className="panel-controls trait-connections-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Trait Connections</h2>
                    <ToolDescriptionPopover dialogLabel="Trait Connections tool description">
                        <p>
                            Upload a JSON file with objects containing
                            &quot;name&quot; and &quot;traits&quot; fields.
                            Nodes will be arranged in a circle, and nodes
                            sharing traits will be connected with colored arcs.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                    {hasData && (
                        <>
                            <button
                                type="button"
                                className="generic-button"
                                onClick={handleDownload}
                            >
                                Download SVG
                            </button>
                            <button
                                type="button"
                                className="generic-button"
                                onClick={handleDownloadHtml}
                            >
                                Download HTML
                            </button>
                            <button
                                type="button"
                                className="generic-button"
                                onClick={clearData}
                            >
                                Clear data
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="trait-connections"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />

                <CollapsibleToolGroup title="Data source" icon={FileUp}>
                    <div className="field-row">
                        <label className="field-label">
                            JSON Data File
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".json"
                                onChange={handleFileChange}
                                style={{ width: "100%" }}
                            />
                        </label>
                    </div>

                    {error && (
                        <div className="field-row">
                            <p style={{ color: "red" }}>{error}</p>
                        </div>
                    )}
                </CollapsibleToolGroup>

                {hasData && (
                    <>
                        <CollapsibleToolGroup title="Summary" icon={Tags}>
                            <div className="field-row">
                                <p className="upload-info">
                                    Loaded {data.length} object
                                    {data.length !== 1 ? "s" : ""}
                                    {uniqueTraitCount > 0 && (
                                        <>
                                            {" "}
                                            with {uniqueTraitCount} unique trait
                                            {uniqueTraitCount !== 1 ? "s" : ""}
                                        </>
                                    )}
                                    {arcs.length > 0 && (
                                        <>
                                            {" "}
                                            and {arcs.length} connection
                                            {arcs.length !== 1 ? "s" : ""}
                                        </>
                                    )}
                                </p>
                            </div>

                            {uniqueTraitCount > 0 && (
                                <div className="field-row">
                                    <label className="field-label">
                                        Trait Colors
                                        <div
                                            style={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: "0.5rem",
                                                marginTop: "0.5rem",
                                            }}
                                        >
                                            {uniqueTraits.map((trait) => (
                                                <div
                                                    key={trait}
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "0.25rem",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: "20px",
                                                            height: "20px",
                                                            backgroundColor:
                                                                traitColors[
                                                                    trait
                                                                ],
                                                            border: "1px solid #333",
                                                            borderRadius: "3px",
                                                        }}
                                                    />
                                                    <span
                                                        style={{
                                                            fontSize: "12px",
                                                        }}
                                                    >
                                                        {trait}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </label>
                                </div>
                            )}
                        </CollapsibleToolGroup>

                        <CollapsibleToolGroup
                            title="Nodes & arcs"
                            icon={Palette}
                        >
                            <div className="field-row">
                                <label className="field-label">
                                    Node size (px)
                                    <DebouncedInput
                                        type="number"
                                        min={1}
                                        step={1}
                                        value={nodeSize}
                                        onChange={handleNodeSizeChange}
                                    />
                                </label>
                            </div>

                            <div className="field-row">
                                <label className="field-label">
                                    Node color
                                    <input
                                        type="color"
                                        value={nodeColor}
                                        onChange={(e) =>
                                            setNodeColor(e.target.value)
                                        }
                                        style={{
                                            width: "100%",
                                            height: "40px",
                                        }}
                                    />
                                </label>
                            </div>

                            <div className="field-row">
                                <label className="field-label">
                                    Arc stroke width (px)
                                    <DebouncedInput
                                        type="number"
                                        min={0.5}
                                        step={0.5}
                                        value={arcStrokeWidth}
                                        onChange={handleArcStrokeWidthChange}
                                    />
                                </label>
                            </div>

                            <div className="field-row">
                                <label className="field-label">
                                    Bezier angle (degrees)
                                    <DebouncedInput
                                        type="number"
                                        min={0}
                                        max={90}
                                        step={5}
                                        value={bezierAngle}
                                        onChange={handleBezierAngleChange}
                                    />
                                </label>
                            </div>
                        </CollapsibleToolGroup>

                        <CollapsibleToolGroup title="Layout" icon={LayoutGrid}>
                            <div className="field-row">
                                <label className="field-label">
                                    Initial layout
                                    <select
                                        value={initialLayoutType}
                                        onChange={(e) =>
                                            setInitialLayoutType(
                                                e.target.value as
                                                    | "circle"
                                                    | "grid"
                                                    | "hexagonal",
                                            )
                                        }
                                        className="anchor-select"
                                    >
                                        <option value="circle">Circle</option>
                                        <option value="grid">
                                            Grid (equidistant)
                                        </option>
                                        <option value="hexagonal">
                                            Hexagonal Grid
                                        </option>
                                    </select>
                                </label>
                            </div>

                            <div className="field-row">
                                <CheckboxField
                                    checked={reorderByTraitCount}
                                    onChange={setReorderByTraitCount}
                                    label="Reorder by trait count (spread items with many traits evenly)"
                                />
                            </div>

                            <div className="field-row">
                                <CheckboxField
                                    checked={enableProximityLayout}
                                    onChange={setEnableProximityLayout}
                                    label="Enable proximity layout (nodes with shared traits cluster together)"
                                />
                            </div>

                            {enableProximityLayout && (
                                <div className="field-row">
                                    <label className="field-label">
                                        Proximity strength (%)
                                        <DebouncedInput
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={5}
                                            value={proximityStrength}
                                            onChange={
                                                handleProximityStrengthChange
                                            }
                                        />
                                    </label>
                                </div>
                            )}
                        </CollapsibleToolGroup>

                        <CollapsibleToolGroup title="Labels" icon={Type}>
                            <div className="field-row">
                                <CheckboxField
                                    checked={showNodeLabels}
                                    onChange={setShowNodeLabels}
                                    label="Show node labels"
                                />
                            </div>

                            <div className="field-row">
                                <CheckboxField
                                    checked={showArcLabels}
                                    onChange={setShowArcLabels}
                                    label="Show arc labels"
                                />
                            </div>

                            {showArcLabels && (
                                <>
                                    <div className="field-row">
                                        <label className="field-label">
                                            Arc label font size (px)
                                            <DebouncedInput
                                                type="number"
                                                min={6}
                                                max={24}
                                                step={1}
                                                value={arcLabelFontSize}
                                                onChange={(v) => {
                                                    const numValue =
                                                        typeof v === "number"
                                                            ? v
                                                            : Number(v);
                                                    if (
                                                        !Number.isFinite(
                                                            numValue,
                                                        ) ||
                                                        numValue < 6 ||
                                                        numValue > 24
                                                    )
                                                        return;
                                                    setArcLabelFontSize(
                                                        numValue,
                                                    );
                                                }}
                                            />
                                        </label>
                                    </div>
                                    <div className="field-row">
                                        <CheckboxField
                                            checked={arcLabelUseArcColor}
                                            onChange={setArcLabelUseArcColor}
                                            label="Use arc color for labels (instead of black)"
                                        />
                                    </div>
                                </>
                            )}
                        </CollapsibleToolGroup>
                        <CollapsibleToolGroup title="Send to tool" icon={Share2}>
                            <SendSvgToToolMenu
                                disabled={!hasData}
                                getSvg={() => ({
                                    name: "trait-connections-export.svg",
                                    content: buildExportSvgString(),
                                })}
                                allowedSlots={["path", "object", "primary"]}
                                excludeTargetPanels={["trait-connections"]}
                            />
                        </CollapsibleToolGroup>
                    </>
                )}
            </div>
        </div>
    );
}
